import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsClockwise,
  CalendarBlank,
  CheckCircle,
  Plus,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  applyStaffShiftTemplate,
  getStaffShiftTemplateSnapshot,
  upsertStaffShiftTemplate,
  upsertStaffShiftTemplateSlot,
} from "@/lib/scheduling";
import { fetchRatioCapacityForecast } from "@/lib/ratioCapacity";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const COVERAGE_META = {
  within_configured_ratio: { label: "Coverage set", className: "bg-success-soft text-success" },
  no_scheduled_children: { label: "No children planned", className: "bg-neutral-100 text-neutral-600" },
  needs_configuration: { label: "Setup needed", className: "bg-warning-soft text-warning" },
  needs_review: { label: "Review data", className: "bg-warning-soft text-warning" },
  capacity_exceeded: { label: "Capacity exceeded", className: "bg-danger-soft text-danger" },
  out_of_ratio: { label: "Coverage gap", className: "bg-danger-soft text-danger" },
};

const COVERAGE_REASONS = {
  missing_ratio_policy: "No active ratio policy.",
  missing_licensed_capacity: "Licensed capacity is not configured.",
  scheduled_child_missing_date_of_birth: "A scheduled child is missing a date of birth.",
  scheduled_child_without_matching_age_rule: "A scheduled child has no matching age rule.",
  mixed_age_requires_review: "Mixed ages require review.",
  licensed_capacity_exceeded: "Licensed capacity is exceeded.",
  configured_group_size_exceeded: "Configured group size is exceeded.",
  qualified_staff_gap: "More designated staff are needed.",
  scheduled_staff_not_designated_for_ratio: "Some scheduled staff are not designated for the ratio.",
  staff_scheduled_in_multiple_classrooms: "A staff member is scheduled in two classrooms at once.",
};

const inputClass = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-neutral-50";

function dateInputValue(value = new Date()) {
  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(value, amount) {
  const date = new Date(value);
  date.setDate(date.getDate() + amount);
  return date;
}

function timeLabel(value) {
  if (!value) return "Time not set";
  const [rawHours = "00", rawMinutes = "00"] = String(value).slice(0, 5).split(":");
  const hours = Number(rawHours);
  if (!Number.isFinite(hours)) return String(value);
  return `${hours % 12 || 12}:${rawMinutes} ${hours >= 12 ? "PM" : "AM"}`;
}

function rangeLabel(start, end) {
  if (!start || !end) return "Choose dates";
  const from = new Date(`${start}T12:00:00`);
  const to = new Date(`${end}T12:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "Choose dates";
  const options = { month: "short", day: "numeric" };
  return `${from.toLocaleDateString(undefined, options)} – ${to.toLocaleDateString(undefined, { ...options, year: "numeric" })}`;
}

function outcomeSummary(outcome) {
  const parts = [];
  if (outcome.createdShiftCount) parts.push(`${outcome.createdShiftCount} added`);
  if (outcome.skippedExistingCount) parts.push(`${outcome.skippedExistingCount} already planned`);
  if (outcome.skippedConflictCount) parts.push(`${outcome.skippedConflictCount} staff conflict${outcome.skippedConflictCount === 1 ? "" : "s"}`);
  if (outcome.skippedTimeOffCount) parts.push(`${outcome.skippedTimeOffCount} approved leave conflict${outcome.skippedTimeOffCount === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "No weekly shifts fell in this date range.";
}

function emptySlot() {
  return {
    id: null,
    // A weekly template may influence many upcoming shifts. Require an
    // administrator to make the staff/classroom assignment deliberately
    // instead of silently choosing whichever record sorts first.
    profileId: "",
    classroomId: "",
    weekday: 1,
    startsAt: "09:00",
    endsAt: "17:00",
    roleLabel: "",
    notes: "",
    isActive: true,
  };
}

export default function StaffShiftTemplateWorkspace({ organizationId, staff, classrooms, weekStart, onApplied }) {
  const [rangeStart, setRangeStart] = useState(() => dateInputValue(weekStart));
  const [rangeEnd, setRangeEnd] = useState(() => dateInputValue(addDays(weekStart, 27)));
  const [coverageDate, setCoverageDate] = useState(() => dateInputValue(weekStart));
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [slotEditor, setSlotEditor] = useState(null);
  const [busy, setBusy] = useState("");
  const [lastOutcome, setLastOutcome] = useState(null);
  const [coverageRows, setCoverageRows] = useState([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState("");

  useEffect(() => {
    setRangeStart(dateInputValue(weekStart));
    setRangeEnd(dateInputValue(addDays(weekStart, 27)));
    setCoverageDate(dateInputValue(weekStart));
  }, [weekStart]);

  const loadTemplates = useCallback(async () => {
    if (!organizationId) {
      setSnapshot(null);
      setError("Templates need a live HoneyAcorn center.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const next = await getStaffShiftTemplateSnapshot({ from: rangeStart, to: rangeEnd });
      setSnapshot(next);
      setSelectedTemplateId((current) => {
        if (current && next.templates.some((template) => template.id === current)) return current;
        return next.templates[0]?.id || null;
      });
    } catch (loadError) {
      setError(loadError?.message || "HoneyAcorn could not load recurring templates.");
    } finally {
      setLoading(false);
    }
  }, [organizationId, rangeEnd, rangeStart]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const selectedTemplate = useMemo(
    () => snapshot?.templates.find((template) => template.id === selectedTemplateId) || null,
    [snapshot?.templates, selectedTemplateId],
  );

  useEffect(() => {
    setTemplateName(selectedTemplate?.name || "");
    setSlotEditor(null);
  }, [selectedTemplate?.id, selectedTemplate?.name]);

  const run = async (key, action) => {
    setBusy(key);
    try {
      return await action();
    } catch (actionError) {
      const message = actionError?.message || "The scheduling change could not be saved.";
      toast.error(message);
      return null;
    } finally {
      setBusy("");
    }
  };

  async function createTemplate(event) {
    event.preventDefault();
    const name = newTemplateName.trim();
    if (!name) {
      toast.error("Give this weekly template a name.");
      return;
    }
    const saved = await run("create-template", () => upsertStaffShiftTemplate({ name, isActive: true }));
    if (!saved) return;
    setNewTemplateName("");
    setSelectedTemplateId(saved.id);
    toast.success("Weekly template created");
    await loadTemplates();
  }

  async function saveTemplate(event) {
    event.preventDefault();
    if (!selectedTemplate) return;
    const name = templateName.trim();
    if (!name) {
      toast.error("Give this weekly template a name.");
      return;
    }
    const saved = await run("save-template", () => upsertStaffShiftTemplate({
      templateId: selectedTemplate.id,
      name,
      isActive: selectedTemplate.isActive,
    }));
    if (!saved) return;
    toast.success("Template saved");
    await loadTemplates();
  }

  async function toggleTemplate() {
    if (!selectedTemplate) return;
    const saved = await run("toggle-template", () => upsertStaffShiftTemplate({
      templateId: selectedTemplate.id,
      name: selectedTemplate.name,
      isActive: !selectedTemplate.isActive,
    }));
    if (!saved) return;
    toast.success(saved.isActive ? "Template activated" : "Template paused");
    await loadTemplates();
  }

  async function saveSlot(values) {
    if (!selectedTemplate) return;
    if (!values.profileId || !values.classroomId || values.weekday === "" || !values.startsAt || !values.endsAt) {
      toast.error("Choose a teacher, classroom, weekday, and shift times.");
      return;
    }
    if (values.endsAt <= values.startsAt) {
      toast.error("The shift must end after it starts.");
      return;
    }
    const saved = await run("save-slot", () => upsertStaffShiftTemplateSlot({
      slotId: values.id,
      templateId: selectedTemplate.id,
      profileId: values.profileId,
      classroomId: values.classroomId,
      weekday: Number(values.weekday),
      startsAt: values.startsAt,
      endsAt: values.endsAt,
      roleLabel: values.roleLabel,
      notes: values.notes,
      isActive: values.isActive,
    }));
    if (!saved) return;
    setSlotEditor(null);
    toast.success(values.id ? "Weekly shift saved" : "Weekly shift added");
    await loadTemplates();
  }

  async function applyTemplate() {
    if (!selectedTemplate) return;
    if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) {
      toast.error("Choose a valid date range.");
      return;
    }
    const outcome = await run("apply-template", () => applyStaffShiftTemplate({
      templateId: selectedTemplate.id,
      rangeStart,
      rangeEnd,
    }));
    if (!outcome) return;
    setLastOutcome(outcome);
    toast.success(outcome.createdShiftCount ? "Planned shifts added" : "No new shifts were added");
    await Promise.all([loadTemplates(), onApplied?.()]);
  }

  const loadCoverage = useCallback(async () => {
    if (!coverageDate) return;
    setCoverageLoading(true);
    setCoverageError("");
    try {
      setCoverageRows(await fetchRatioCapacityForecast({ date: coverageDate }));
    } catch (loadError) {
      setCoverageError(loadError?.message || "HoneyAcorn could not load planned coverage.");
    } finally {
      setCoverageLoading(false);
    }
  }, [coverageDate]);

  if (!organizationId) {
    return (
      <section data-testid="admin-scheduling-templates-unavailable" className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
        <CalendarBlank size={25} weight="duotone" className="mx-auto text-neutral-400" />
        <h2 className="mt-3 font-heading text-base font-semibold text-neutral-900">Templates need a live center</h2>
        <p className="mt-1 text-sm text-neutral-600">Connect to a HoneyAcorn center to create or apply recurring planned shifts.</p>
      </section>
    );
  }

  if (loading) {
    return <div data-testid="admin-scheduling-templates-loading" className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /></div>;
  }

  if (error) {
    return (
      <section data-testid="admin-scheduling-templates-error" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex gap-3">
          <WarningCircle size={20} weight="duotone" className="mt-0.5 shrink-0 text-amber-800" />
          <div>
            <h2 className="font-heading text-base font-semibold text-amber-950">Recurring templates are unavailable</h2>
            <p className="mt-1 text-sm leading-6 text-amber-900">{error}</p>
            <button type="button" onClick={loadTemplates} className="mt-3 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100">Try again</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="admin-scheduling-templates" className="space-y-5">
      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-hover">Planned coverage</p>
            <h2 className="mt-1 font-heading text-lg font-semibold text-neutral-900">Weekly templates, made explicit</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-neutral-700">
              Create a repeatable weekly pattern, then choose the upcoming dates to add. HoneyAcorn never changes an existing shift, actual time, or approved leave automatically.
            </p>
          </div>
          <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 shadow-sm">
            {snapshot?.generatedShiftCount || 0} generated shift{snapshot?.generatedShiftCount === 1 ? "" : "s"} in this window
          </span>
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(230px,0.65fr)_minmax(0,1.35fr)]">
        <aside className="rounded-2xl border border-neutral-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3 px-1 pb-2">
            <h3 className="font-heading text-sm font-semibold text-neutral-900">Templates</h3>
            <button type="button" onClick={loadTemplates} aria-label="Refresh templates" className="rounded-full p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"><ArrowsClockwise size={15} weight="bold" /></button>
          </div>
          {snapshot?.templates.length ? (
            <div className="space-y-1">
              {snapshot.templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  data-testid={`admin-scheduling-template-${template.id}`}
                  data-active={selectedTemplate?.id === template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`w-full rounded-xl p-3 text-left transition ${selectedTemplate?.id === template.id ? "bg-primary text-white" : "hover:bg-neutral-50"}`}
                >
                  <span className="block truncate text-sm font-semibold">{template.name}</span>
                  <span className={`mt-1 block text-xs ${selectedTemplate?.id === template.id ? "text-white/80" : "text-neutral-500"}`}>
                    {template.activeSlotCount} active weekly shift{template.activeSlotCount === 1 ? "" : "s"} · {template.isActive ? "Active" : "Paused"}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="px-1 py-3 text-sm leading-6 text-neutral-500">No weekly template yet. Start with the pattern your center already uses.</p>
          )}
          <form onSubmit={createTemplate} className="mt-3 border-t border-neutral-100 pt-3">
            <label className="sr-only" htmlFor="new-weekly-template">New template name</label>
            <input id="new-weekly-template" data-testid="admin-scheduling-template-name" value={newTemplateName} onChange={(event) => setNewTemplateName(event.target.value)} placeholder="Opening coverage" className={inputClass} />
            <button type="submit" disabled={busy === "create-template"} className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-primary/30 bg-white px-3 py-2 text-sm font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60">
              <Plus size={15} weight="bold" />
              {busy === "create-template" ? "Creating…" : "New template"}
            </button>
          </form>
        </aside>

        {selectedTemplate ? (
          <main className="space-y-5">
            <section className="rounded-2xl border border-neutral-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <form onSubmit={saveTemplate} className="min-w-[220px] flex-1">
                  <label htmlFor="selected-weekly-template" className="text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Template name</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <input id="selected-weekly-template" data-testid="admin-scheduling-template-edit-name" value={templateName} onChange={(event) => setTemplateName(event.target.value)} className={`${inputClass} min-w-[200px] flex-1`} />
                    <button type="submit" disabled={busy === "save-template"} className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">{busy === "save-template" ? "Saving…" : "Save"}</button>
                  </div>
                </form>
                <button type="button" data-testid="admin-scheduling-template-toggle" disabled={busy === "toggle-template"} onClick={toggleTemplate} className={`rounded-full px-3 py-2 text-xs font-semibold disabled:opacity-60 ${selectedTemplate.isActive ? "bg-primary/10 text-primary-hover hover:bg-primary/15" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
                  {selectedTemplate.isActive ? "Active · pause" : "Paused · activate"}
                </button>
              </div>
              <p className="mt-3 text-xs leading-5 text-neutral-500">Edits apply only to future materialization. Shifts already added to the calendar stay as explicit records for a human to review or change.</p>
            </section>

            <section className="rounded-2xl border border-neutral-200 bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-heading text-base font-semibold text-neutral-900">Weekly shifts</h3>
                  <p className="mt-1 text-sm text-neutral-600">One slot is one local weekday assignment. Overlapping weekly assignments for the same teacher are rejected.</p>
                </div>
                <button type="button" data-testid="admin-scheduling-template-add-slot" onClick={() => setSlotEditor(emptySlot())} disabled={!staff.length || !classrooms.length} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">
                  <Plus size={15} weight="bold" /> Add weekly shift
                </button>
              </div>

              {!staff.length || !classrooms.length ? <p className="mt-4 rounded-xl bg-neutral-50 p-3 text-sm text-neutral-600">Add teacher-role staff and a classroom before creating a weekly shift.</p> : null}

              {slotEditor ? <SlotEditor slot={slotEditor} staff={staff} classrooms={classrooms} saving={busy === "save-slot"} onChange={setSlotEditor} onCancel={() => setSlotEditor(null)} onSave={() => saveSlot(slotEditor)} /> : null}

              {selectedTemplate.slots.length ? (
                <ul className="mt-4 divide-y divide-neutral-100 rounded-xl border border-neutral-100">
                  {selectedTemplate.slots.map((slot) => (
                    <li key={slot.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3">
                      <div className="min-w-[180px] flex-1">
                        <p className="text-sm font-semibold text-neutral-900">{WEEKDAYS[slot.weekday] || "Weekday"} · {timeLabel(slot.startsAt)} – {timeLabel(slot.endsAt)}</p>
                        <p className="mt-0.5 text-xs text-neutral-500">{slot.profileName} · {slot.classroomName}{slot.roleLabel ? ` · ${slot.roleLabel}` : ""}</p>
                        {slot.notes ? <p className="mt-1 text-xs text-neutral-500">{slot.notes}</p> : null}
                      </div>
                      <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${slot.isActive ? "bg-primary/10 text-primary-hover" : "bg-neutral-100 text-neutral-500"}`}>{slot.isActive ? "Active" : "Paused"}</span>
                      <button type="button" data-testid={`admin-scheduling-template-slot-${slot.id}`} onClick={() => setSlotEditor({ ...slot })} className="rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">Edit</button>
                    </li>
                  ))}
                </ul>
              ) : !slotEditor ? <p className="mt-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-5 text-center text-sm text-neutral-600">Add the first weekly shift before applying this template.</p> : null}
            </section>

            <section className="rounded-2xl border border-neutral-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-base font-semibold text-neutral-900">Add to the calendar</h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">This is a deliberate, bounded action. Existing shifts stay unchanged; staff conflicts and approved time off are skipped for review.</p>
                </div>
                <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-600">No auto-scheduling</span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-neutral-700">Start date<input data-testid="admin-scheduling-template-range-start" type="date" min={dateInputValue()} value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} className={`${inputClass} mt-1`} /></label>
                <label className="text-xs font-semibold text-neutral-700">End date<input data-testid="admin-scheduling-template-range-end" type="date" min={rangeStart || dateInputValue()} value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} className={`${inputClass} mt-1`} /></label>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button type="button" data-testid="admin-scheduling-template-apply" disabled={!selectedTemplate.isActive || !selectedTemplate.activeSlotCount || busy === "apply-template"} onClick={applyTemplate} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"><CalendarBlank size={16} weight="duotone" />{busy === "apply-template" ? "Adding shifts…" : `Add ${rangeLabel(rangeStart, rangeEnd)}`}</button>
                {!selectedTemplate.isActive ? <p className="text-xs text-neutral-500">Activate this template before adding shifts.</p> : null}
                {selectedTemplate.isActive && !selectedTemplate.activeSlotCount ? <p className="text-xs text-neutral-500">Add an active weekly shift before applying.</p> : null}
              </div>
              {lastOutcome ? <p data-testid="admin-scheduling-template-outcome" aria-live="polite" className="mt-4 rounded-xl bg-primary/5 p-3 text-sm text-primary-hover"><CheckCircle size={16} weight="duotone" className="mr-1.5 inline-block align-text-bottom" />{outcomeSummary(lastOutcome)}. Review coverage before the day begins.</p> : null}
            </section>
          </main>
        ) : (
          <section className="rounded-2xl border border-dashed border-neutral-300 bg-white p-8 text-center">
            <CalendarBlank size={28} weight="duotone" className="mx-auto text-neutral-400" />
            <h2 className="mt-3 font-heading text-lg font-semibold text-neutral-900">Start with one weekly pattern</h2>
            <p className="mx-auto mt-1 max-w-lg text-sm leading-6 text-neutral-600">Name the coverage pattern your center already follows, add the weekly shifts, then decide exactly when to place it on the calendar.</p>
          </section>
        )}
      </div>

      <CoveragePanel date={coverageDate} setDate={setCoverageDate} rows={coverageRows} loading={coverageLoading} error={coverageError} onRefresh={loadCoverage} />

      {snapshot?.applications?.length ? (
        <section className="rounded-2xl border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold text-neutral-900">Recent template actions</h3>
          <ul className="mt-3 divide-y divide-neutral-100 rounded-xl border border-neutral-100">
            {snapshot.applications.slice(0, 6).map((application) => (
              <li key={application.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 p-3">
                <div>
                  <p className="text-sm font-semibold text-neutral-900">{application.templateName} · {rangeLabel(application.rangeStart, application.rangeEnd)}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">{outcomeSummary(application)} · applied by {application.appliedByName}</p>
                </div>
                <time className="text-xs text-neutral-400" dateTime={application.appliedAt || undefined}>{application.appliedAt ? new Date(application.appliedAt).toLocaleDateString() : ""}</time>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function SlotEditor({ slot, staff, classrooms, saving, onChange, onCancel, onSave }) {
  return (
    <div data-testid="admin-scheduling-template-slot-editor" className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-heading text-sm font-semibold text-neutral-900">{slot.id ? "Edit weekly shift" : "Add weekly shift"}</h4>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700"><input type="checkbox" checked={slot.isActive} onChange={(event) => onChange({ ...slot, isActive: event.target.checked })} className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary" />Active</label>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="text-xs font-semibold text-neutral-700">Teacher<select data-testid="admin-scheduling-template-slot-staff" value={slot.profileId} onChange={(event) => onChange({ ...slot, profileId: event.target.value })} className={`${inputClass} mt-1`}><option value="">Choose teacher</option>{staff.map((member) => <option key={member.id} value={member.id}>{member.full_name}</option>)}</select></label>
        <label className="text-xs font-semibold text-neutral-700">Classroom<select data-testid="admin-scheduling-template-slot-classroom" value={slot.classroomId} onChange={(event) => onChange({ ...slot, classroomId: event.target.value })} className={`${inputClass} mt-1`}><option value="">Choose classroom</option>{classrooms.map((classroom) => <option key={classroom.id} value={classroom.id}>{classroom.name}</option>)}</select></label>
        <label className="text-xs font-semibold text-neutral-700">Weekday<select data-testid="admin-scheduling-template-slot-weekday" value={slot.weekday} onChange={(event) => onChange({ ...slot, weekday: Number(event.target.value) })} className={`${inputClass} mt-1`}>{WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}</select></label>
        <label className="text-xs font-semibold text-neutral-700">Role label<input data-testid="admin-scheduling-template-slot-role" value={slot.roleLabel || ""} onChange={(event) => onChange({ ...slot, roleLabel: event.target.value })} placeholder="lead, float, sub…" className={`${inputClass} mt-1`} /></label>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-700">Start<input data-testid="admin-scheduling-template-slot-start" type="time" value={String(slot.startsAt || "").slice(0, 5)} onChange={(event) => onChange({ ...slot, startsAt: event.target.value })} className={`${inputClass} mt-1`} /></label>
        <label className="text-xs font-semibold text-neutral-700">End<input data-testid="admin-scheduling-template-slot-end" type="time" value={String(slot.endsAt || "").slice(0, 5)} onChange={(event) => onChange({ ...slot, endsAt: event.target.value })} className={`${inputClass} mt-1`} /></label>
      </div>
      <label className="mt-3 block text-xs font-semibold text-neutral-700">Notes<textarea data-testid="admin-scheduling-template-slot-notes" rows={2} value={slot.notes || ""} onChange={(event) => onChange({ ...slot, notes: event.target.value })} className={`${inputClass} mt-1`} /></label>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">Cancel</button>
        <button type="button" data-testid="admin-scheduling-template-slot-save" onClick={onSave} disabled={saving} className="rounded-full bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">{saving ? "Saving…" : "Save weekly shift"}</button>
      </div>
    </div>
  );
}

function CoveragePanel({ date, setDate, rows, loading, error, onRefresh }) {
  const attention = rows.filter((row) => ["out_of_ratio", "capacity_exceeded", "needs_configuration", "needs_review"].includes(row.out_status));
  return (
    <section data-testid="admin-scheduling-template-coverage" className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Explainable coverage</p>
          <h3 className="mt-1 font-heading text-base font-semibold text-neutral-900">Review the plan before the day begins</h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-neutral-600">This uses the existing Ratio & Capacity forecast: configured child schedules, the planned shifts above, designated staff, breaks, and center rules. It flags missing inputs instead of claiming compliance.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs font-semibold text-neutral-700">Forecast date<input data-testid="admin-scheduling-template-coverage-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} className={`${inputClass} mt-1`} /></label>
          <button type="button" data-testid="admin-scheduling-template-coverage-refresh" onClick={onRefresh} disabled={loading} className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">{loading ? "Checking…" : "Check coverage"}</button>
        </div>
      </div>
      {error ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{error}</div> : null}
      {!error && !loading && rows.length === 0 ? <p className="mt-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-600">Choose a date and check coverage when you are ready. No separate ratio or compliance state is created here.</p> : null}
      {!error && rows.length > 0 ? <div className="mt-4 space-y-3"><p className={`text-sm ${attention.length ? "text-amber-800" : "text-neutral-600"}`}>{attention.length ? `${attention.length} planned coverage item${attention.length === 1 ? "" : "s"} needs attention.` : "No coverage item is currently flagged for attention."}</p><ul className="divide-y divide-neutral-100 rounded-xl border border-neutral-100">{rows.map((row) => { const meta = COVERAGE_META[row.out_status] || { label: "Review data", className: "bg-amber-50 text-amber-900" }; const reasons = (row.out_reason_codes || []).map((reason) => COVERAGE_REASONS[reason] || reason).join(" "); return <li key={`${row.out_classroom_id}-${row.out_starts_at}`} className="flex flex-wrap items-start justify-between gap-3 p-3"><div><p className="text-sm font-semibold text-neutral-900">{row.out_classroom_name} · {timeLabel(row.out_starts_at)} – {timeLabel(row.out_ends_at)}</p><p className="mt-0.5 text-xs text-neutral-500">{row.out_scheduled_children ?? 0} planned children · {row.out_qualified_staff_scheduled ?? 0}{row.out_required_staff == null ? " designated staff" : ` / ${row.out_required_staff} designated staff`}</p>{reasons ? <p className="mt-1 max-w-xl text-xs leading-5 text-neutral-600">{reasons}</p> : null}</div><span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${meta.className}`}>{meta.label}</span></li>; })}</ul></div> : null}
      <Link to="/admin/ratio-capacity" className="mt-4 inline-flex text-sm font-semibold text-primary-hover hover:underline">Open Ratio &amp; Capacity for setup and full forecast →</Link>
    </section>
  );
}
